import { WindowState } from '../state';
import { createLayoutPicker } from './LayoutPicker';
import { createDimensionInputs } from './DimensionInputs';
import { createSectionTypeSelector } from './SectionTypeSelector';
import { createMaterialSelector } from './MaterialSelector';
import { createGlazingSelector } from './GlazingSelector';
import { createRoedenSelector } from './RoedenSelector';
import { createHardwareSelector } from './HardwareSelector';
import { createMirrorFlipControls } from './MirrorFlipControls';

export class Panel {
  private root: HTMLElement;
  private state: WindowState;

  constructor(container: HTMLElement, state: WindowState) {
    this.root = container;
    this.state = state;
    this.build();
  }

  private build(): void {
    this.root.innerHTML = '';

    // Header
    const header = document.createElement('div');
    header.className = 'panel-header';
    header.textContent = 'Window Configurator';
    this.root.appendChild(header);

    // Dimensions
    this.addSection('Dimensions', createDimensionInputs(this.state), true);

    // Layout
    const layoutContent = document.createElement('div');
    layoutContent.appendChild(createLayoutPicker(this.state, () => this.rebuild()));
    layoutContent.appendChild(createMirrorFlipControls(this.state));
    this.addSection('Layout', layoutContent, true);

    // Per-section selectors
    for (let i = 0; i < this.state.config.sections.length; i++) {
      const badge = this.state.config.sections[i].type;
      this.addSection(
        `Section ${i + 1}`,
        createSectionTypeSelector(this.state, i),
        false,
        badge,
        i + 1,
      );
    }

    // Frame & Finish
    this.addSection('Frame & Finish', createMaterialSelector(this.state), false);

    // Glazing
    this.addSection('Glazing', createGlazingSelector(this.state), false);

    // Roeden
    this.addSection('Bars (Roeden)', createRoedenSelector(this.state), false);

    // Hardware
    this.addSection('Hardware', createHardwareSelector(this.state), false);
  }

  rebuild(): void {
    this.build();
  }

  private addSection(
    title: string,
    content: HTMLElement,
    openByDefault: boolean,
    badge?: string,
    sectionNumber?: number,
  ): void {
    const section = document.createElement('div');
    section.className = 'panel-section';
    if (openByDefault) section.classList.add('open');

    const header = document.createElement('div');
    header.className = 'panel-section-header';

    const titleSpan = document.createElement('span');
    titleSpan.className = 'panel-section-title';

    if (sectionNumber !== undefined) {
      const chip = document.createElement('span');
      chip.className = 'section-num-chip';
      chip.textContent = String(sectionNumber);
      titleSpan.appendChild(chip);
    }

    const titleText = document.createElement('span');
    titleText.textContent = title;
    titleSpan.appendChild(titleText);

    if (badge) {
      const badgeSpan = document.createElement('span');
      badgeSpan.className = 'section-badge';
      badgeSpan.textContent = ` — ${badge}`;
      titleSpan.appendChild(badgeSpan);
    }

    const chevron = document.createElement('span');
    chevron.className = 'chevron';
    chevron.textContent = '\u25B6';

    header.appendChild(titleSpan);
    header.appendChild(chevron);

    header.addEventListener('click', () => {
      section.classList.toggle('open');
    });

    const body = document.createElement('div');
    body.className = 'panel-section-body';
    body.appendChild(content);

    section.appendChild(header);
    section.appendChild(body);
    this.root.appendChild(section);
  }
}
